import { UserRole } from './role.model';

/**
 * The parts of the app whose visibility depends on the role.
 *
 * - 'logistics' is Red logística: locations and vehicles.
 * - 'workforce' is Fuerza de trabajo: drivers and gestores.
 * - 'clients' is Clientes: contracts and users.
 * - The rest are the four screens under Reportes, one each.
 *
 * Dashboard, live map, trips and settings are not listed: every role reaches them.
 */
export type AppSection =
  | 'logistics'
  | 'workforce'
  | 'clients'
  | 'tripReports'
  | 'customReports'
  | 'driverReports'
  | 'auditReports';

/** Every role the app has rules for, plus the one it could not read. */
const EVERYONE: UserRole[] = [
  'administrator',
  'coordinator',
  'callCenter',
  'clientLogistics',
  'clientSecurity',
  'unknown',
];

/**
 * Who may see and open each section. Single source of truth: the route guard, the menu and
 * the dashboard's shortcuts all read this, so they cannot drift apart.
 *
 * - The three core sections and Historial de cambios are the administrator's alone.
 * - The driver report is closed to Seguridad de cliente only. 'Supervisor' is the same role
 *   under its old name (see role.model), so it is covered by the same entry.
 * - The trip and custom reports are open to every role. 'unknown' is named there on purpose
 *   and nowhere else: a user whose role could not be read gets the least a role can see,
 *   which is these two reports, and is locked out of everything restricted by construction.
 */
const SECTION_ROLES: Record<AppSection, UserRole[]> = {
  logistics: ['administrator'],
  workforce: ['administrator'],
  clients: ['administrator'],
  auditReports: ['administrator'],
  driverReports: ['administrator', 'coordinator', 'callCenter', 'clientLogistics'],
  tripReports: EVERYONE,
  customReports: EVERYONE,
};

/** Whether `role` may see and open `section`. */
export function canAccessSection(role: UserRole, section: AppSection): boolean {
  return SECTION_ROLES[section].includes(role);
}
